import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Toast from '../components/Toast.jsx'
import BackButton from '../components/BackButton.jsx'
import { useAuth } from '../auth/AuthProvider.jsx'
import { fetchHistoricoPedidos } from '../data/syncManager.js'
import { drawPDFHeader, drawTableHeader, drawTableRow, checkNewPage } from '../data/pdfUtils.js'
import { jsPDF } from 'jspdf'

function formatDate(ts) {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export default function HistoricoComprasPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  
  const [historico, setHistorico] = useState([])
  const [toast, setToast] = useState(null)
  const [toastType, setToastType] = useState('info')
  const [busy, setBusy] = useState(false)
  
  // Filtros
  const [filtroData, setFiltroData] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('todos')

  async function reload() {
    setBusy(true)
    try {
      const lista = await fetchHistoricoPedidos(true) // Forçar refresh do cache
      
      // Remover todos os registros com status 'enviado-para-compras' (criados antes da correção)
      const listaFiltrada = lista.filter(item => {
        const status = item.dados?.status || item.status
        return status !== 'enviado-para-compras'
      })
      
      const ordenada = listaFiltrada
        .sort((a, b) => (b.finalizadoEm || b.dataEnvio || b.criadoEm || 0) - (a.finalizadoEm || a.dataEnvio || a.criadoEm || 0))
        .slice(0, 100)
      setHistorico(ordenada)
    } catch (err) {
      console.error('Erro ao carregar histórico:', err)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    reload()
  }, [])

  // Aplicar filtros
  const historicoFiltrado = historico.filter(item => {
    // Filtro de data
    if (filtroData) {
      const dataItem = formatDate(item.finalizadoEm || item.dataEnvio || item.criadoEm)
      if (!dataItem.includes(filtroData)) return false
    }
    
    // Filtro de status - buscar de dados.status ou status
    const status = item.dados?.status || item.status
    if (filtroStatus !== 'todos') {
      if (status !== filtroStatus) return false
    }
    
    return true
  })

  async function exportarPDF() {
    setBusy(true)
    try {
      const doc = new jsPDF('landscape')

      await drawPDFHeader(doc, {
        titulo: 'HISTÓRICO DE COMPRAS',
        subtitulo: `${historicoFiltrado.length} registro(s)`,
        usuario: session?.usuarioNome || 'Sistema',
        infoExtra: `Filtro: ${filtroStatus === 'todos' ? 'Todos' : filtroStatus}`,
        landscape: true
      })

      let y = 75
      const colWidths = [15, 50, 80, 40, 30, 30, 30]
      const headers = ['#', 'Pedido', 'Produto', 'Status', 'Qtd Sol.', 'Qtd Comp.', 'Data']

      historicoFiltrado.forEach((pedido, idx) => {
        pedido.itens?.forEach((item, itemIdx) => {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          const statusText = item.status === 'concluido' ? 'Concluído' :
                            item.status === 'parcial' ? 'Parcial' :
                            item.status === 'rejeitado' ? 'Rejeitado' : 'Pendente'
          drawTableRow(doc, [
            String(idx + 1),
            pedido.numero,
            item.produtoNome,
            statusText,
            String(item.quantidadeSolicitada),
            item.quantidadeComprada ? String(item.quantidadeComprada) : '—',
            formatDate(pedido.dataFinalizacao)
          ], y, colWidths, 'center')
          y += 7
        })

        if (!pedido.itens || pedido.itens.length === 0) {
          y = checkNewPage(doc, y, 180, headers, colWidths, 'center')
          drawTableRow(doc, [
            String(idx + 1),
            pedido.numero,
            '—',
            pedido.status,
            '—',
            '—',
            formatDate(pedido.dataFinalizacao)
          ], y, colWidths, 'center')
          y += 7
        }
      })

      doc.save(`historico-compras-${new Date().toISOString().split('T')[0]}.pdf`)

      setToastType('success')
      setToast('PDF exportado com sucesso!')
    } catch (err) {
      console.error('Erro ao exportar PDF:', err)
      setToastType('error')
      setToast('Falha ao exportar PDF.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} type={toastType} onClose={() => setToast(null)} />
      
      <BackButton />

      <div className="rounded-2xl cm-card p-4">
        <div className="text-sm font-semibold">📋 Histórico de Compras</div>
        <div className="mt-1 text-xs text-white/70">
          Visualize compras finalizadas
        </div>
      </div>

      {/* Botão voltar para compras */}
      <button
        onClick={() => navigate('/app/compras')}
        className="w-full rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold"
      >
        ← Voltar para Compras
      </button>

      {/* Filtros */}
      <div className="rounded-2xl cm-card p-4 space-y-3">
        <div className="text-xs font-semibold">Filtros</div>
        
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] text-white/60 mb-1">Data</div>
            <input
              type="text"
              value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
              placeholder="DD/MM/AAAA"
              className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm outline-none"
            />
          </div>
          <div>
            <div className="text-[10px] text-white/60 mb-1">Status</div>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="w-full rounded-xl bg-white/10 px-3 py-2 text-sm"
            >
              <option value="todos">Todos</option>
              <option value="comprado">Comprado</option>
              <option value="parcial">Parcial</option>
              <option value="rejeitado">Rejeitado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Botão Exportar PDF */}
      <button
        onClick={exportarPDF}
        disabled={busy || historicoFiltrado.length === 0}
        className="w-full rounded-xl bg-accent-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
      >
        {busy ? 'Gerando PDF…' : '📄 Exportar para PDF'}
      </button>

      {/* Lista */}
      {historicoFiltrado.length === 0 ? (
        <div className="rounded-2xl cm-card p-6 text-center">
          <div className="text-4xl mb-2">📭</div>
          <div className="text-sm text-white/80">Nenhum histórico encontrado</div>
          <div className="mt-1 text-xs text-white/60">
            {filtroData || filtroStatus !== 'todos' 
              ? 'Tente ajustar os filtros.' 
              : 'Compras confirmadas aparecerão aqui.'}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-white/50">
            {historicoFiltrado.length} registro(s)
          </div>
          
          {historicoFiltrado.map((pedido) => {
            const status = pedido.dados?.status || pedido.status
            const usuarioConfirmacaoNome = pedido.dados?.usuarioConfirmacaoNome || pedido.usuarioNome || '—'
            return (
            <div key={pedido.id} className="rounded-2xl cm-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">{pedido.numero}</div>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${
                      status === 'comprado' ? 'bg-emerald-500/20 text-emerald-300' :
                      status === 'parcial' ? 'bg-amber-500/20 text-amber-300' :
                      status === 'rejeitado' ? 'bg-red-500/20 text-red-300' :
                      'bg-red-500/20 text-red-300'
                    }`}>
                      {status === 'comprado' ? '✓ Comprado' :
                       status === 'parcial' ? '◐ Parcial' :
                       status === 'rejeitado' ? '✗ Rejeitado' :
                       '✗ Cancelado'}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-white/70">
                    Finalizado: {formatDate(pedido.finalizadoEm || pedido.dataEnvio || pedido.criadoEm)}
                  </div>
                  <div className="mt-1 text-xs text-white/60">
                    Por: {usuarioConfirmacaoNome}
                  </div>
                </div>
              </div>

              {/* Itens do pedido */}
              <div className="mt-3 space-y-1 border-t border-white/10 pt-2">
                <div className="text-[10px] text-white/50 uppercase tracking-wide">
                  {pedido.itens?.length || 0} item(s)
                </div>
                {pedido.itens?.slice(0, 3).map((item) => (
                  <div key={item.id} className="text-xs text-white/70 flex items-center gap-2">
                    <span>{item.produtoNome}</span>
                    <span className="text-white/40">
                      {item.quantidadeComprada 
                        ? `${item.quantidadeComprada}/${item.quantidadeSolicitada}` 
                        : item.quantidadeSolicitada}
                    </span>
                    {item.status === 'concluido' && <span className="text-emerald-400">✓</span>}
                    {item.status === 'rejeitado' && <span className="text-red-400">✗</span>}
                  </div>
                ))}
                {pedido.itens?.length > 3 && (
                  <div className="text-xs text-white/50">
                    + {pedido.itens.length - 3} itens mais...
                  </div>
                )}
              </div>
            </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
